import {
  format as formatDate,
  isAfter,
  isBefore,
  isDate,
  isEqual,
  isValid,
} from 'date-fns';

import { divmod } from './number';

const MILLISECONDS = 1000;

/**
 * 格式化日期，若格式化失敗則回傳空字串
 * @param date - 要格式化的日期
 * @param format - 日期格式，預設為 'yyyy/LL/dd HH:mm:ss'
 * @returns 格式化後的日期字串或空字串
 * @example formatDateOrEmpty(new Date(), 'yyyy/LL/dd') // '2021/07/06'
 */
export function formatDateOrEmpty (date: Date | string | number, format = 'yyyy/LL/dd HH:mm:ss') {
  if (!date || date === null || date === undefined) {
    return '';
  }
  try {
    const parsedDate = new Date(date);
    if (!isDate(parsedDate) || !isValid(parsedDate)) {
      return '';
    }
    return formatDate(parsedDate, format);
  } catch (error) {
    console.error(error);
    return '';
  }
}

/**
 * 將日期轉換為 Unix 時間戳（秒）
 * @param date - 要轉換的日期
 * @returns Unix 時間戳或 null
 * @example toUnixTimestamp('2021-11-02T10:18:17+08:00') // 1635819497
 */
export function toUnixTimestamp (date: Date | string | number) {
  if (!date) { return null; }

  const t = new Date(date);

  if (!isDate(t) || !isValid(t)) { return null; }

  return t.getTime() / MILLISECONDS;
}

/**
 * 檢查日期是否在指定的時間範圍內（包含邊界）
 * @param dateToCompare - 要比較的日期
 * @param start - 開始日期
 * @param end - 結束日期
 * @returns 是否在範圍內
 */
export function isBetween (dateToCompare: Date | string | number, start: Date | string | number, end: Date | string | number) {
  // Check if any of the dates are null, undefined, or invalid
  if (!dateToCompare || !start || !end) {
    return false;
  }

  try {
    const compareDate = new Date(dateToCompare);
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (!isValid(compareDate) || !isValid(startDate) || !isValid(endDate)) {
      return false;
    }

    return (isBefore(startDate, compareDate) || isEqual(startDate, compareDate)) &&
      (isAfter(endDate, compareDate) || isEqual(endDate, compareDate));
  } catch {
    return false;
  }
}

/**
 * 將秒數轉換成時:分:秒格式的字串（支援小數）
 * @param secondsDuration - 秒數（可為負數和小數）
 * @param options - 選項，precision 為小數位數（預設 2）
 * @returns 格式化後的時間字串，如 "01:30:45.50" 或 "-00:01:30"
 * @example toHoursString(3661) // "01:01:01"
 * @example toHoursString(12.3) // "00:00:12.30"
 * @example toHoursString(-90) // "-00:01:30"
 */
export const toHoursString = (secondsDuration: number, { precision = 2 }: { precision?: number } = {}) => {
  const nf = new Intl.NumberFormat('en', { maximumFractionDigits: precision, useGrouping: false });
  const seconds = parseFloat(nf.format(Math.abs(secondsDuration)));

  const [hours, minutesLeft] = divmod(seconds, 3600);

  const [minutes, secondsLeft] = divmod(minutesLeft, 60);
  const resultWithoutSign = [hours, minutes, secondsLeft]
    .map((n) => n < 10 ? `0${nf.format(n)}` : nf.format(n))
    .join(':');

  if (Math.sign(secondsDuration) < 0) {
    return `-${resultWithoutSign}`;
  } else {
    return resultWithoutSign;
  }
};
